import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { type ApiError, type CareerOpening, type CareerOpeningStatus } from '@vello/api-client';
import {
  CAREER_FORM_LIMITS,
  careerAuthority,
  careerCityName,
  careerContentOf,
  careerFormFromOpening,
  careerFormValid,
  careerLinkInvalid,
  careerNextStatuses,
  emptyCareerForm,
  fourEyesCountries,
  formatMarketInstant,
  marketsOfCountry,
  openingsInScope,
  type CareerForm,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { PersonName } from '../components/person-name';
import { DrawerStack } from '../components/drawer-stack';
import { useScopeZone } from '../components/scope-zone';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, EmptyResult, Field, InlineRow, KeyValueList, ListTable, Notice, PageHeader, SegmentedControl, Select, Stack, Text, TextArea, TextInput, type Column } from '@vello/web-theme';

/**
 * Configurações › Vagas do site - the job openings the institutional site's Careers page lists.
 *
 * - The TOP-BAR SCOPE is the only country / city selector: the list reads the scope's country (or every country of the registry for a
 *   platform-level operator) and, with a city selected, shows that city's openings and the country-wide ones. A country picker exists
 *   only inside the "Nova vaga" form, and only where the scope spans countries.
 * - A row opens the opening's RIGHT-SIDE DRAWER (its words, where it is, who wrote it, its status). "Nova vaga" - the screen's ONE
 *   primary action - and "Editar vaga" open the editor in that same drawer. The city is a list of the registered cities of the
 *   opening's country plus "País inteiro".
 * - Status moves behind an in-app confirmation (never a native dialog) that says what it does on the site: "Abrir vaga" puts it on
 *   the site, "Encerrar vaga" takes it off, "Voltar para rascunho" takes it off and unlocks the words (a closed opening is frozen).
 * - Without `career_opening:manage` the screen is read-only; the server authorizes every call at the opening's country.
 */
export function CareerOpeningsScreen(): ReactElement {
  const { runtime, markets, scope, grants } = useAdmin();
  const { t } = useT();
  const { zone, lang } = useScopeZone();
  const { v, fail, explain } = useVocab();
  const { confirm } = useConsole();
  const { mayManage } = careerAuthority(grants);
  const scopeCountry = scope.current.country;
  const scopeCity = scope.current.city;
  const countries = useMemo(() => (scopeCountry !== undefined ? [scopeCountry] : fourEyesCountries(markets)), [scopeCountry, markets]);
  const [all, setAll] = useState<readonly CareerOpening[] | undefined>(undefined);
  /** An opening id, `'new'` for the create form, or undefined (the drawer is closed). */
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [mode, setMode] = useState<'detail' | 'edit'>('detail');
  const [form, setForm] = useState<CareerForm>(emptyCareerForm(''));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);

  const refusal = (e: ApiError): string => explain(e) ?? t('careers.reason.other', { code: fail(e) });

  const load = useCallback(async (): Promise<void> => {
    if (countries.length === 0) return;
    const answers = await Promise.all(countries.map((cc) => runtime.careers.list(cc)));
    const failed = answers.find((a) => !a.ok);
    // A failed read is an error, never an empty list (an unavailable source is never a zero).
    if (failed !== undefined && !failed.ok) {
      setAll(undefined);
      setMessage({ tone: 'danger', text: refusal(failed.error) });
      return;
    }
    setAll(answers.flatMap((a) => (a.ok ? a.value.openings : [])));
  }, [countries, runtime]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => (all === undefined ? undefined : openingsInScope(all, scopeCity)), [all, scopeCity]);
  const current = selected !== undefined && selected !== 'new' ? all?.find((o) => o.openingId === selected) : undefined;
  const when = (instant: string | undefined): string => formatMarketInstant(instant, zone, lang);
  const place = (o: Pick<CareerOpening, 'country' | 'marketId'>): string => {
    const city = careerCityName(o, markets);
    const withCountry = scopeCountry === undefined;
    if (city === undefined) return withCountry ? t('careers.place.countryOf', { country: o.country }) : t('careers.place.country');
    return withCountry ? t('careers.place.cityOf', { country: o.country, city }) : city;
  };
  const tone = (s: CareerOpeningStatus): 'neutral' | 'success' | 'warning' => (s === 'OPEN' ? 'success' : s === 'DRAFT' ? 'warning' : 'neutral');

  const open = (o: CareerOpening): void => {
    setSelected(o.openingId);
    setMode('detail');
    setForm(careerFormFromOpening(o));
    setMessage(undefined);
  };
  const startNew = (): void => {
    setSelected('new');
    setMode('edit');
    setForm(emptyCareerForm(scopeCountry ?? countries[0] ?? '', scopeCity ?? ''));
    setMessage(undefined);
  };
  const close = (): void => {
    setSelected(undefined);
    setMessage(undefined);
  };
  const cancelEdit = (): void => {
    if (current === undefined) {
      close();
      return;
    }
    setForm(careerFormFromOpening(current));
    setMode('detail');
    setMessage(undefined);
  };

  const save = async (): Promise<void> => {
    setBusy(true);
    setMessage(undefined);
    const content = careerContentOf(form);
    const res = selected === 'new' ? await runtime.careers.create({ country: form.country, ...content }) : current !== undefined ? await runtime.careers.edit(current.openingId, content) : undefined;
    setBusy(false);
    if (res === undefined) return;
    if (!res.ok) {
      setMessage({ tone: 'danger', text: refusal(res.error) });
      return;
    }
    setMessage({ tone: 'success', text: t('careers.saved') });
    setSelected(res.value.openingId);
    setForm(careerFormFromOpening(res.value));
    setMode('detail');
    await load();
  };

  /** Every status change says what it does on the site, then asks (an in-app confirmation, never a native dialog). */
  const changeStatus = async (to: CareerOpeningStatus): Promise<void> => {
    if (current === undefined) return;
    const key = to === 'OPEN' ? 'open' : to === 'CLOSED' ? 'close' : 'draft';
    const answer = await confirm({
      title: t(`careers.confirm.${key}.title`),
      body: t(`careers.confirm.${key}.body`),
      confirmLabel: t(`careers.confirm.${key}.yes`),
      cancelLabel: t('careers.cancel'),
      tone: to === 'OPEN' ? 'primary' : 'danger',
    });
    if (!answer.confirmed) return;
    setBusy(true);
    setMessage(undefined);
    const res = await runtime.careers.setStatus(current.openingId, to);
    setBusy(false);
    if (!res.ok) {
      setMessage({ tone: 'danger', text: refusal(res.error) });
      return;
    }
    setMessage({ tone: 'success', text: t(`careers.done.${to}`) });
    setForm(careerFormFromOpening(res.value.opening));
    await load();
  };

  const columns: readonly Column<CareerOpening>[] = [
    {
      key: 'title',
      header: t('careers.col.title'),
      width: 'minmax(260px, 2fr)',
      render: (o) => (
        <Stack gap={1}>
          <Text as="strong">{o.title}</Text>
          <Text role="caption" tone="tertiary">{o.team} · {o.employmentType}</Text>
        </Stack>
      ),
    },
    { key: 'place', header: t('careers.col.place'), width: 'minmax(160px, 1fr)', render: (o) => <Text>{place(o)}</Text> },
    { key: 'state', header: t('careers.col.state'), width: 'minmax(120px, .8fr)', render: (o) => <Badge tone={tone(o.status)}>{v('careers.opening_status', o.status)}</Badge> },
    { key: 'opened', header: t('careers.col.opened'), width: 'minmax(150px, 1fr)', render: (o) => (o.openedAt !== undefined ? <Text className="tnum">{when(o.openedAt)}</Text> : <Text role="caption" tone="tertiary">—</Text>) },
  ];

  const editable = mode === 'edit' && mayManage && (selected === 'new' || (current !== undefined && current.status !== 'CLOSED'));
  const cities = marketsOfCountry(markets, form.country);
  const set = <K extends keyof CareerForm>(key: K, value: CareerForm[K]): void => setForm((f) => ({ ...f, [key]: value }));
  const detail = current;

  return (
    <div>
      <PageHeader
        title={t('careers.title')}
        subtitle={t('careers.subtitle')}
        actions={mayManage ? <Button variant="primary" onClick={startNew} disabled={countries.length === 0} data-testid="careers-new">{t('careers.new')}</Button> : undefined}
      />
      {!mayManage && <Notice tone="neutral">{t('careers.readonly')}</Notice>}
      {message !== undefined && selected === undefined && <Notice tone={message.tone}>{message.text}</Notice>}

      {rows !== undefined && rows.length === 0 && <EmptyResult title={t('careers.empty.title')} detail={t('careers.empty.detail')} />}
      {rows !== undefined && rows.length > 0 && (
        <ListTable columns={columns} rows={rows} rowKey={(o) => o.openingId} onRowClick={open} {...(selected !== undefined && selected !== 'new' ? { selectedKey: selected } : {})} minWidth={760} />
      )}

      <Drawer open={selected !== undefined} onClose={close} labelledBy="careers-drawer-title">
        {selected !== undefined && mode === 'edit' && (
          <>
            <DrawerHeader titleId="careers-drawer-title" title={t(selected === 'new' ? 'careers.new' : 'careers.edit')} onClose={close} closeLabel={t('ui.close')} />
            <DrawerBody>
              <DrawerStack>
                {message !== undefined && <Notice tone={message.tone}>{message.text}</Notice>}
                {selected === 'new' && scopeCountry === undefined && countries.length > 1 && (
                  <Field label={t('careers.country')} htmlFor="careers-country">
                    <SegmentedControl ariaLabel={t('careers.country')} options={countries.map((c) => ({ id: c, label: c }))} value={form.country} onChange={(c) => setForm((f) => ({ ...f, country: c, marketId: '' }))} />
                  </Field>
                )}
                <Field label={t('careers.field.city')} htmlFor="careers-city" hint={t('careers.hint.city')}>
                  <Select id="careers-city" value={form.marketId} disabled={!editable} onChange={(e) => set('marketId', e.target.value)} data-testid="careers-city">
                    <option value="">{t('careers.place.country')}</option>
                    {cities.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </Select>
                </Field>
                <Field label={t('careers.field.title')} htmlFor="careers-title">
                  <TextInput id="careers-title" value={form.title} maxLength={CAREER_FORM_LIMITS.title} disabled={!editable} onChange={(e) => set('title', e.target.value)} data-testid="careers-title" />
                </Field>
                <Field label={t('careers.field.team')} htmlFor="careers-team">
                  <TextInput id="careers-team" value={form.team} maxLength={CAREER_FORM_LIMITS.team} disabled={!editable} onChange={(e) => set('team', e.target.value)} data-testid="careers-team" />
                </Field>
                <Field label={t('careers.field.employmentType')} htmlFor="careers-type" hint={t('careers.hint.employmentType')}>
                  <TextInput id="careers-type" value={form.employmentType} maxLength={CAREER_FORM_LIMITS.employmentType} disabled={!editable} onChange={(e) => set('employmentType', e.target.value)} data-testid="careers-type" />
                </Field>
                <Field label={t('careers.field.summary')} htmlFor="careers-summary" hint={t('careers.hint.summary', { max: CAREER_FORM_LIMITS.summary })}>
                  <TextInput id="careers-summary" value={form.summary} maxLength={CAREER_FORM_LIMITS.summary} disabled={!editable} onChange={(e) => set('summary', e.target.value)} data-testid="careers-summary" />
                </Field>
                <Field label={t('careers.field.description')} htmlFor="careers-description" hint={t('careers.hint.description')}>
                  <TextArea id="careers-description" className="vello-ab-bodyarea" value={form.description} maxLength={CAREER_FORM_LIMITS.description} disabled={!editable} onChange={(e) => set('description', e.target.value)} data-testid="careers-description" />
                </Field>
                <Field label={t('careers.field.applyUrl')} htmlFor="careers-apply" hint={careerLinkInvalid(form.applyUrl) ? t('careers.link.invalid') : t('careers.hint.applyUrl')}>
                  <TextInput id="careers-apply" value={form.applyUrl} maxLength={CAREER_FORM_LIMITS.applyUrl} disabled={!editable} onChange={(e) => set('applyUrl', e.target.value)} data-testid="careers-apply" />
                </Field>
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="ghost" onClick={cancelEdit} data-testid="careers-cancel">{t('careers.cancel')}</Button>
              {editable && <Button variant="primary" onClick={() => void save()} disabled={busy || !careerFormValid(form)} data-testid="careers-save">{t(selected === 'new' ? 'careers.create' : 'careers.save')}</Button>}
            </DrawerFooter>
          </>
        )}
        {selected !== undefined && mode === 'detail' && detail !== undefined && (
          <>
            <DrawerHeader titleId="careers-drawer-title" title={detail.title} subtitle={`${detail.team} · ${detail.employmentType}`} actions={<Badge tone={tone(detail.status)}>{v('careers.opening_status', detail.status)}</Badge>} onClose={close} closeLabel={t('ui.close')} />
            <DrawerBody>
              <DrawerStack>
                {message !== undefined && <Notice tone={message.tone}>{message.text}</Notice>}
                <Notice tone="neutral">{t(`careers.note.${detail.status}`)}</Notice>
                <KeyValueList
                  items={[
                    { label: t('careers.kv.place'), value: place(detail) },
                    { label: t('careers.kv.team'), value: detail.team },
                    { label: t('careers.kv.employmentType'), value: detail.employmentType },
                    { label: t('careers.kv.apply'), value: detail.applyUrl ?? t('careers.apply.chat'), ...(detail.applyUrl !== undefined ? { mono: true } : {}) },
                    ...(detail.openedAt !== undefined ? [{ label: t('careers.kv.openedAt'), value: <Text className="tnum">{when(detail.openedAt)}</Text> }] : []),
                    ...(detail.closedAt !== undefined ? [{ label: t('careers.kv.closedAt'), value: <Text className="tnum">{when(detail.closedAt)}</Text> }] : []),
                    { label: t('careers.kv.createdBy'), value: <InlineRow gap={2} align="center" wrap><PersonName id={detail.createdBy} /><Text role="caption" tone="tertiary" className="tnum">{when(detail.createdAt)}</Text></InlineRow> },
                    { label: t('careers.kv.updatedBy'), value: <InlineRow gap={2} align="center" wrap><PersonName id={detail.updatedBy} /><Text role="caption" tone="tertiary" className="tnum">{when(detail.updatedAt)}</Text></InlineRow> },
                  ]}
                />
                <Field label={t('careers.field.summary')} htmlFor="careers-summary-preview">
                  <TextInput id="careers-summary-preview" value={detail.summary} readOnly data-testid="careers-summary-preview" />
                </Field>
                <Field label={t('careers.field.description')} htmlFor="careers-preview">
                  <TextArea id="careers-preview" className="vello-ab-bodyarea" value={detail.description} readOnly data-testid="careers-preview" />
                </Field>
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="ghost" onClick={close} data-testid="careers-close">{t('ui.close')}</Button>
              {mayManage && detail.status !== 'CLOSED' && <Button variant="secondary" onClick={() => setMode('edit')} disabled={busy} data-testid="careers-edit">{t('careers.edit')}</Button>}
              {mayManage && careerNextStatuses(detail.status).map((to) => (
                <Button key={to} variant={to === 'OPEN' ? 'primary' : 'secondary'} onClick={() => void changeStatus(to)} disabled={busy} data-testid={`careers-to-${to.toLowerCase()}`}>
                  {t(to === 'OPEN' ? 'careers.action.open' : to === 'CLOSED' ? 'careers.action.close' : 'careers.action.draft')}
                </Button>
              ))}
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </div>
  );
}
